// Debounced backup of drafts → the private repo. OPFS-SQLite is the
// working cache; the private repo is canonical (the 4G git-canonical
// pattern). We snapshot the BKA-managed private tree — a README plus
// each draft's markdown + meta — on a 30s debounce after edits, matching
// foundry's auto-push cadence.
//
// Snapshot (not incremental) is intentional: BKA owns the private repo's
// content, so a deleted draft simply drops out of the next snapshot. The
// commit is content-idempotent — an unchanged tree is a no-op.

import type { Draft } from "./storage/drafts";
import type { SiteFile } from "./site-render";
import { commitFiles } from "./github-commit";

function privateReadme(): string {
  return `# BKA — private working repo

Drafts, scratch, and work-in-progress. Nobody sees this but you. Managed by the
BKA app — edit drafts in BKA, not by hand. When you publish a draft, the finished
post graduates to your public repo.
`;
}

export function renderPrivateTree(drafts: Draft[]): SiteFile[] {
  const files: SiteFile[] = [{ path: "README.md", content: privateReadme() }];
  for (const d of drafts) {
    files.push({ path: `drafts/${d.id}.md`, content: d.body });
    files.push({
      path: `drafts/${d.id}.meta.json`,
      content: JSON.stringify({
        id: d.id,
        title: d.title,
        summary: d.summary,
        tags: JSON.parse(d.tags || "[]"),
        published_slug: d.published_slug,
        created_at: d.created_at,
        updated_at: d.updated_at,
      }, null, 2) + "\n",
    });
  }
  return files;
}

export async function backupDrafts(token: string, privateRepo: string, drafts: Draft[]): Promise<void> {
  const files = renderPrivateTree(drafts);
  await commitFiles(token, privateRepo, files, "BKA: back up drafts");
}

// ─── debounce scheduler ─────────────────────────────────────────────

let timer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 30_000;

/** Schedule a best-effort backup. Repeated calls within the window
 *  collapse to one push. `provide` is called at fire time to fetch the
 *  freshest drafts + auth, so the snapshot reflects the latest edits. */
export function scheduleBackup(
  provide: () => Promise<{ token: string; privateRepo: string; drafts: Draft[] } | null>,
  onResult?: (ok: boolean, err?: string) => void,
): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void (async () => {
      try {
        const ctx = await provide();
        if (!ctx) return;
        await backupDrafts(ctx.token, ctx.privateRepo, ctx.drafts);
        onResult?.(true);
      } catch (e) {
        // Non-blocking: a failed backup never interrupts writing. The
        // next edit reschedules; local SQLite still has the work.
        onResult?.(false, e instanceof Error ? e.message : String(e));
      }
    })();
  }, DEBOUNCE_MS);
}

/** Force any pending backup to run now (e.g. on explicit "back up"). */
export function flushBackup(): void {
  if (timer) { clearTimeout(timer); timer = null; }
}
